import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import { v4 as uuidv4 } from 'uuid';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import AuditSubTable from '@components/table/AuditSubTable';
import AuditTableSearchInputGroup from '@components/smartAdminUI/input/AuditTableSearchInputGroup';
import AgentAvatar from '@components/table/avatar/AgentAvatar';
import { getAuditData } from '../../redux/selectors/AuditSelector';
import translateColumns, { translateSort } from '../dataTable/dataTranslate';
import DataTable from '../dataTable';

const AuditTable = ({ sendFilteredDataToParent }) => {
  const auditData = useSelector(getAuditData);
  const [usingAdvancedSearch, setUsingAdvancedSearch] = useState(false);
  const [searchObject, setSearchObject] = useState({});
  const [subTransactionData, setSubTransactionData] = useState([]);
  const distinctTransactionData = useMemo(() => {
    if (isEmpty(auditData)) return [];
    if (isEmpty(searchObject)) {
      setSubTransactionData(auditData);
      return Object.values(
        auditData.flat()
          .reduce((result, obj) => {
            const {
              TransactionID,
              RecordUniqueID,
              ChangeTimeStamp,
              RealUser,
              ImpersonatedUser,
              Transactiontype,
            } = obj;
            const distinctData = result;
            distinctData[TransactionID] = distinctData[TransactionID] || {
              UniqueId: uuidv4(),
              TransactionID,
              RecordUniqueID,
              ChangeTimeStamp,
              RealUser,
              ImpersonatedUser,
              Transactiontype,
            };
            return distinctData;
          }, {}),
      );
    }

    const filteredDisplayData = auditData.flat().filter(row => {
      let searchFound;
      if (usingAdvancedSearch) {
        const searchFields = {
          transactionId: row.TransactionID.toLowerCase(),
          changedBy: row.RealUser.toLowerCase(),
          impersonating: row.ImpersonatedUser.toLowerCase(),
          transactionType: row.Transactiontype.toLowerCase(),
          fieldType: row.ClassName.toLowerCase(),
          fieldName: row.FieldName.toLowerCase(),
          changedFrom: String(row.OldValue).toLowerCase(),
          changedTo: String(row.NewValue).toLowerCase(),
        };
        searchFound = ((isEmpty(searchObject.transactionId) || searchFields.transactionId.toLowerCase().includes(searchObject.transactionId.toLowerCase()))
          && (isEmpty(searchObject.changedBy) || searchFields.changedBy.toLowerCase().includes(searchObject.changedBy.toLowerCase()))
          && (isEmpty(searchObject.impersonating) || searchFields.impersonating.toLowerCase().includes(searchObject.impersonating.toLowerCase()))
          && (isEmpty(searchObject.transactionType) || searchFields.transactionType.toLowerCase().includes(searchObject.transactionType.toLowerCase()))
          && (isEmpty(searchObject.fieldType) || searchFields.fieldType.toLowerCase().includes(searchObject.fieldType.toLowerCase()))
          && (isEmpty(searchObject.fieldName) || searchFields.fieldName.toLowerCase().includes(searchObject.fieldName.toLowerCase()))
          && (isEmpty(searchObject.changedFrom) || searchFields.changedFrom.toLowerCase().includes(searchObject.changedFrom.toLowerCase()))
          && (isEmpty(searchObject.changedTo) || searchFields.changedTo.toLowerCase().includes(searchObject.changedTo.toLowerCase())))
          || Object.values(searchObject).every(value => isEmpty(value));
      } else {
        const searchFields = [
          row.TransactionID.toLowerCase(),
          row.ChangeTimeStamp.toLowerCase(),
          row.RealUser.toLowerCase(),
          row.ImpersonatedUser.toLowerCase(),
          row.Transactiontype.toLowerCase(),
          row.ClassName.toLowerCase(),
          row.FieldName.toLowerCase(),
          String(row.OldValue).toLowerCase(),
          String(row.NewValue).toLowerCase(),
        ];
        searchFound = searchFields.some(field => field.toLowerCase().includes(searchObject.searchText.toLowerCase()));
      }
      return searchFound;
    });
    setSubTransactionData(filteredDisplayData);
    const distinctTransactions = (Object.values(
      filteredDisplayData.flat()
        .reduce((result, obj) => {
          const {
            TransactionID,
            RecordUniqueID,
            ChangeTimeStamp,
            RealUser,
            ImpersonatedUser,
            Transactiontype,
          } = obj;
          const distinctData = result;
          distinctData[TransactionID] = distinctData[TransactionID] || {
            UniqueId: uuidv4(),
            TransactionID,
            RecordUniqueID,
            ChangeTimeStamp,
            RealUser,
            ImpersonatedUser,
            Transactiontype,
          };
          return distinctData;
        }, {}),
    ));
    return distinctTransactions;
  }, [auditData, searchObject]);

  useEffect(() => {
    if (isEmpty(subTransactionData)) return;
    sendFilteredDataToParent(subTransactionData);
  }, [subTransactionData]);

  const hdrSortClasses = (_column, sortOrder) => `sorting_${sortOrder}`;

  const columns = [
    {
      dataField: 'TransactionID',
      text: 'ID',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
    },
    {
      dataField: 'ChangeTimeStamp',
      text: 'Date',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      sortingFn: 'myDateTime',
    },
    {
      dataField: 'Transactiontype',
      text: 'Type',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '100px' },
    },
    {
      dataField: 'RealUser',
      text: 'Changed By',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '100px' },
      classes: 'text-center',
      formatter: cellContent => <AgentAvatar fullName={cellContent} showFullName={false} />,
    },
    {
      dataField: 'ImpersonatedUser',
      text: 'Impersonating',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '100px' },
      classes: 'text-center',
      formatter: (cellContent, column) => (column.RealUser === cellContent ? '' : <AgentAvatar fullName={cellContent} showFullName={false} />),
    },
  ];

  const handleSearch = object => {
    setSearchObject(object);
  };

  const expandRow = useCallback(() => ({
    renderer: row => {
      const subTransactionDisplayData = subTransactionData.filter(subTransaction => subTransaction.TransactionID === row.TransactionID).map(item => ({ ...item, UniqueId: uuidv4() }));
      return (<AuditSubTable displayData={subTransactionDisplayData} />);
    },
    showExpandColumn: true,
    expandByColumnOnly: true,
    classes: 'text-center',
    expandHeaderColumnRenderer: ({ isAnyExpands }) => {
      if (isAnyExpands) {
        return (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-collapseAll-button" style={{ margin: 0 }}>Collapse All</Tooltip>}
          >
            <button type="button" className="btn btn-sm btn-primary">
              <i className="far fa-minus" />
            </button>
          </OverlayTrigger>
        );
      }
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tooltip-expandAll-button" style={{ margin: 0 }}>Expand All</Tooltip>}
        >
          <button type="button" className="btn btn-primary btn-sm">
            <i className="far fa-plus" />
          </button>
        </OverlayTrigger>
      );
    },
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-collapse-button" style={{ margin: 0 }}>Collapse</Tooltip>}
          >
            <button type="button" className="btn btn-sm btn-primary">
              <i className="far fa-chevron-up" />
            </button>
          </OverlayTrigger>
        );
      }
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tooltip-expand-button" style={{ margin: 0 }}>Expand</Tooltip>}
        >
          <button type="button" className="btn btn-sm btn-primary">
            <i className="far fa-chevron-down" />
          </button>
        </OverlayTrigger>
      );
    },
  }), [subTransactionData, searchObject]);

  const defaultSorted = [{
    dataField: 'ChangeTimeStamp',
    order: 'desc',
  }];

  const newCols = useMemo(() => translateColumns(columns), [columns]);

  return (
    <DataTable
      defaultSorted={translateSort(defaultSorted)}
      columns={newCols}
      expandRow={expandRow()}
      allowPagination={false}
      sizePerPage={20}
      data={distinctTransactionData}
      wrapperClasses="table-responsive"
      id="auditTable"
      noDataIndication="No Data Returned."
      classes="dataTable"
      striped
      hover={false}
      searchComponent={() => (
        <AuditTableSearchInputGroup
          setUsingAdvancedSearch={setUsingAdvancedSearch}
          onSearch={handleSearch}
        />
      )}
    />
  );
};

AuditTable.propTypes = {
  sendFilteredDataToParent: PropTypes.func.isRequired,
};

export default AuditTable;
